import {
  PencilLine,
  Trash2,
  User,
  Mail,
  Save,
  X,
  History,
  MailOpen,
  MessageCircle,
  MailCheck,
} from "lucide-react";
import React, { useState, useEffect } from "react";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    userName: "",
    UserEmail: "",
    userRole: "",
    userMembership: "",
    currentBalance: 0,
  });
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    toEmail: "",
    subject: "",
    messageBody: "",
  });
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://server.enjoywiki.com/youtubeai-may/users");
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(`Failed to fetch users: ${err.message}`);
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  const displaySuccessAlert = (message) => {
    setAlertMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 3000);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedAndFilteredUsers = () => {
    let filteredUsers = [...users];
    if (searchTerm) {
      filteredUsers = filteredUsers.filter(
        (user) =>
          (user.userName &&
            user.userName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.UserEmail &&
            user.UserEmail.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.userRole &&
            user.userRole.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (user.userMembership &&
            user.userMembership
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }
    if (sortConfig.key) {
      filteredUsers.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (sortConfig.key === "currentBalance") {
          const valA = aValue || 0;
          const valB = bValue || 0;
          if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
          if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }
        if (aValue < bValue || !aValue)
          return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue || !bValue)
          return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return filteredUsers;
  };

  const sortedAndFilteredUsers = getSortedAndFilteredUsers();
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedAndFilteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );
  const totalPages = Math.ceil(sortedAndFilteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  const getMembershipBadgeStyle = (status) => {
    if (!status) return { backgroundColor: "#6c757d", color: "white" };
    switch (status.toLowerCase()) {
      case "premium":
        return { backgroundColor: "rgba(255, 193, 7, 0.2)", color: "#ffc107" };
      case "basic":
        return { backgroundColor: "rgba(23, 162, 184, 0.2)", color: "#17a2b8" };
      case "free":
        return {
          backgroundColor: "rgba(108, 117, 125, 0.2)",
          color: "#6c757d",
        };
      default:
        return { backgroundColor: "rgba(0, 0, 0, 0.05)", color: "#6c757d" };
    }
  };

  const getRoleBadgeStyle = (role) => {
    if (!role) return { backgroundColor: "#6c757d", color: "white" };
    switch (role.toLowerCase()) {
      case "admin":
        return { backgroundColor: "rgba(220, 53, 69, 0.2)", color: "#dc3545" };
      case "moderator":
        return { backgroundColor: "rgba(255, 193, 7, 0.2)", color: "#ffc107" };
      case "member":
        return { backgroundColor: "rgba(0, 123, 255, 0.2)", color: "#007bff" };
      default:
        return { backgroundColor: "rgba(0, 0, 0, 0.05)", color: "#6c757d" };
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user._id);
    setEditForm({
      userName: user.userName || "",
      UserEmail: user.UserEmail || "",
      userRole: user.userRole || "member",
      userMembership: user.userMembership || "free",
      currentBalance: user.currentBalance || 0,
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: name === "currentBalance" ? Number(value) : value,
    });
  };

  const handleSaveUser = async (userId) => {
    try {
      const response = await fetch(
        `https://server.enjoywiki.com/youtubeai-may/api/users/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editForm),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to update user: ${response.status}`);
      }
      const updatedUsers = users.map((user) =>
        user._id === userId ? { ...user, ...editForm } : user
      );
      setUsers(updatedUsers);
      setEditingUser(null);
      displaySuccessAlert("User updated successfully");
    } catch (err) {
      setError(`Failed to update user: ${err.message}`);
      console.error("Error updating user:", err);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await fetch(
          `https://server.enjoywiki.com/youtubeai-may/api/users/${userId}`,
          {
            method: "DELETE",
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to delete user: ${response.status}`);
        }
        const updatedUsers = users.filter((user) => user._id !== userId);
        setUsers(updatedUsers);
        displaySuccessAlert("User deleted successfully");
      } catch (err) {
        setError(`Failed to delete user: ${err.message}`);
        console.error("Error deleting user:", err);
      }
    }
  };

  const getUserInitials = (user) => {
    if (user.userName) return user.userName.charAt(0).toUpperCase();
    if (user.UserEmail) return user.UserEmail.charAt(0).toUpperCase();
    return "?";
  };

  const getAvatarBgColor = (userId) => {
    const colors = [
      "#54a0ff",
      "#5de2a3",
      "#ff7979",
      "#feca57",
      "#9b59b6",
      "#1dd1a1",
      "#ff9f43",
      "#ee5253",
      "#a29bfe",
      "#7f8c8d",
    ];
    if (!userId) return colors[0];
    const hash = userId
      .split("")
      .reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    return colors[hash % colors.length];
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pageNumbers = [];
    const maxPagesToShow = 5;
    const halfPages = Math.floor(maxPagesToShow / 2);
    let startPage = Math.max(currentPage - halfPages, 1);
    let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(endPage - maxPagesToShow + 1, 1);
    }
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }
    return (
      <nav aria-label="Page navigation">
        <ul className="pagination mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => paginate(currentPage - 1)}
            >
              Prev
            </button>
          </li>
          {pageNumbers.map((page, index) => (
            <li
              key={index}
              className={`page-item ${page === "..." ? "disabled" : ""} ${
                currentPage === page ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => page !== "..." && paginate(page)}
              >
                {page}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => paginate(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  const handleSendMessage = async () => {
    setSendingMessage(true);
    try {
      const response = await fetch("https://server.enjoywiki.com/youtubeai-may/send-message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(messageForm),
      });
      const data = await response.json();
      if (response.ok) {
        displaySuccessAlert("Message sent successfully.");
        setShowMessageModal(false);
      } else {
        alert(data.error || "Failed to send message.");
      }
    } catch (err) {
      console.error("Send message error:", err);
      alert("Error sending message.");
    } finally {
      setSendingMessage(false);
    }
  };

  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedUserLogs, setSelectedUserLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const logsPerPage = 5;
  const totalLogPages = Math.ceil(selectedUserLogs.length / logsPerPage);
  const paginatedLogs = selectedUserLogs.slice(
    (logPage - 1) * logsPerPage,
    logPage * logsPerPage
  );

  const handleLogPageChange = (direction) => {
    if (direction === "prev" && logPage > 1) setLogPage(logPage - 1);
    if (direction === "next" && logPage < totalLogPages)
      setLogPage(logPage + 1);
  };

  return (
    <div
      className="container-fluid py-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      <div style={{ maxWidth: "1600px", margin: "0 auto" }}>
        {/* Page Header */}
        <div className="row mb-4 align-items-center">
          <div className="col">
            <h2 className="fw-bold" style={{ color: "#2c3e50" }}>
              <User className="me-2" style={{ color: "#3498db" }} />
              Users Management
            </h2>
            <p className="text-muted">
              View, manage, and edit all system users.
            </p>
          </div>
        </div>
        {/* Alerts */}
        {showSuccessAlert && (
          <div
            className="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            <strong>Success!</strong> {alertMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowSuccessAlert(false)}
            ></button>
          </div>
        )}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            <strong>Error!</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}
        {/* Main Content Card */}
        <div
          className="card shadow-sm border-0"
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <div
            className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3 px-4"
            style={{ borderBottom: "1px solid #dee2e6" }}
          >
            <h5 className="mb-0 fw-bold" style={{ color: "#495057" }}>
              User Directory
            </h5>
            <div className="d-flex align-items-center mt-2 mt-md-0">
              <div className="input-group" style={{ maxWidth: "280px" }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ borderRadius: "20px 0 0 20px" }}
                />
                <span
                  className="input-group-text bg-white"
                  style={{ borderRadius: "0 20px 20px 0", borderLeft: "none" }}
                >
                  <i className="bi bi-search text-muted"></i>
                </span>
              </div>
              <button
                className="btn btn-outline-primary ms-2"
                style={{ borderRadius: "20px" }}
                onClick={fetchUsers}
              >
                <i className="bi bi-arrow-clockwise me-1"></i> Refresh
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "400px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden"></span>
                </div>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fc" }}>
                        {[
                          { key: "userName", label: "User" },
                          { key: "currentBalance", label: "Remaining Credit" },
                          { key: "userRole", label: "Role" },
                          { key: "userMembership", label: "Membership" },
                          { key: "createdAt", label: "Joined" },
                        ].map((header) => (
                          <th
                            key={header.key}
                            className="px-4 py-3 text-uppercase"
                            style={{
                              cursor: "pointer",
                              fontWeight: "600",
                              color: "#6c757d",
                              fontSize: "0.8rem",
                            }}
                            onClick={() => requestSort(header.key)}
                          >
                            {header.label} {getSortIndicator(header.key)}
                          </th>
                        ))}
                        <th
                          className="px-4 py-3 text-end text-uppercase"
                          style={{
                            fontWeight: "600",
                            color: "#6c757d",
                            fontSize: "0.8rem",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <i
                              className="bi bi-folder-x"
                              style={{ fontSize: "3rem", color: "#ced4da" }}
                            ></i>
                            <h5 className="mt-3">No Users Found</h5>
                            <p className="text-muted">
                              Try adjusting your search or filter.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        currentUsers.map((user) => (
                          <tr key={user._id}>
                            {editingUser === user._id ? (
                              <>
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    name="userName"
                                    value={editForm.userName}
                                    onChange={handleInputChange}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    name="currentBalance"
                                    value={editForm.currentBalance}
                                    onChange={handleInputChange}
                                    style={{ width: "90px" }}
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    className="form-select form-select-sm"
                                    name="userRole"
                                    value={editForm.userRole}
                                    onChange={handleInputChange}
                                  >
                                    <option value="member">Member</option>
                                    
                                    <option value="Admin">Admin</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    className="form-select form-select-sm"
                                    name="userMembership"
                                    value={editForm.userMembership}
                                    onChange={handleInputChange}
                                  >
                                    <option value="free">Free</option>
                                    <option value="basic">Basic</option>
                                    <option value="premium">Premium</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2 text-muted">
                                  {formatDate(user.createdAt)}
                                </td>
                                <td className="px-4 py-2 text-end">
                                  <button
                                    className="btn btn-sm btn-success me-1"
                                    onClick={() => handleSaveUser(user._id)}
                                  >
                                    <Save size={16} />
                                  </button>
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleCancelEdit}
                                  >
                                    <X size={16} />
                                  </button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-2">
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="avatar me-3"
                                      style={{
                                        backgroundColor: getAvatarBgColor(
                                          user._id
                                        ),
                                        color: "white",
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {getUserInitials(user)}
                                    </div>
                                    <div>
                                      <div
                                        className="fw-bold"
                                        style={{ color: "#212529" }}
                                      >
                                        {user.userName || "N/A"}
                                      </div>
                                      <div className="text-muted small">
                                        {user.UserEmail || "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  className="px-4 py-2 fw-bold"
                                  style={{
                                    color:
                                      user.currentBalance >= 0
                                        ? "#20c997"
                                        : "#dc3545",
                                  }}
                                >
                                  {user.currentBalance || 0} Credit
                                </td>
                                <td className="px-4 py-2">
                                  <span
                                    className="badge rounded-pill"
                                    style={getRoleBadgeStyle(user.userRole)}
                                  >
                                    {user.userRole || "Member"}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <span
                                    className="badge rounded-pill"
                                    style={getMembershipBadgeStyle(
                                      user.userMembership
                                    )}
                                  >
                                    {user.userMembership || "Free"}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-muted">
                                  {formatDate(user.createdAt)}
                                </td>
                                <td className="px-4 py-2 text-end">
                                  <div className="btn-group">
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleEditClick(user)}
                                      title="Edit User"
                                    >
                                      <PencilLine size={16} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteUser(user._id)}
                                      title="Delete User"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted small">
                    Showing <strong>{indexOfFirstUser + 1}</strong> to{" "}
                    <strong>
                      {Math.min(indexOfLastUser, sortedAndFilteredUsers.length)}
                    </strong>{" "}
                    of <strong>{sortedAndFilteredUsers.length}</strong> users
                  </div>
                  {renderPagination()}
                </div>
                {showMessageModal && (
                  <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                  >
                    <div className="modal-dialog modal-lg">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">Send Message</h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowMessageModal(false)}
                          ></button>
                        </div>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">To Email</label>
                            <input
                              type="email"
                              className="form-control"
                              value={messageForm.toEmail}
                              onChange={(e) =>
                                setMessageForm({
                                  ...messageForm,
                                  toEmail: e.target.value,
                                })
                              }
                              readOnly
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Subject</label>
                            <input
                              type="text"
                              className="form-control"
                              value={messageForm.subject}
                              onChange={(e) =>
                                setMessageForm({
                                  ...messageForm,
                                  subject: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">
                              Message Body (HTML allowed)
                            </label>
                            <textarea
                              className="form-control"
                              rows="6"
                              placeholder="<h2>Hello</h2><p>This is <strong>bold</strong> text</p>"
                              value={messageForm.messageBody}
                              onChange={(e) =>
                                setMessageForm({
                                  ...messageForm,
                                  messageBody: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            className="btn btn-secondary"
                            onClick={() => setShowMessageModal(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleSendMessage}
                            disabled={sendingMessage}
                          >
                            {sendingMessage ? "Sending..." : "Send"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;